


























































































































































































































































































import store from '@/store';
import {
  Node, Edge, Network, ArangoPath,
} from '@/types';
import {
  computed, defineComponent, onMounted, ref, Ref, watch,
} from '@vue/composition-api';
import api from '@/api';

export default defineComponent({
  name: 'ConnectivityQuery',

  setup() {
    const showMenu = ref(false);
    const showSecondEdge = ref(false);
    const hopsSelection = [1, 2, 3];
    const selectedHops = computed({
      get() {
        return store.state.selectedHops;
      },
      set(value: number) {
        store.commit.setSelectedHops(value);
      },
    });
    const displayedHops = computed(() => 2 * selectedHops.value + 1);
    const loading: Ref<boolean> = ref(false);

    const selectedVariables: Ref<string[]> = ref([]);
    const nodeVariableItems = computed(() => store.getters.nodeVariableItems);
    const edgeVariableItems = computed(() => store.getters.edgeVariableItems);
    const nodeAttributeItems = computed(() => store.state.nodeAttributes);
    const edgeAttributeItems = computed(() => store.state.edgeAttributes);

    const selectedQueryOptions: Ref<string[]> = ref([]);
    const queryOptionItems = ['==', '=~', '!=', '<', '<=', '>', '>='];
    const operatorOptionItems = ['AND', 'OR', 'NOT'];

    const directionalEdges = computed({
      get() {
        return store.state.directionalEdges;
      },
      set(value: boolean) {
        store.commit.setDirectionalEdges(value);
      },
    });

    // Create the object for storing input data
    const queryInput: Ref<{ key: number; value: { label: string; operator: string; input: string }[]; operator: string }[]> = ref([]);
    const edgeMutexs: Ref<{ value: { label: string; operator: string; input: string }[]; operator: string }> = ref({ value: [], operator: '' });

    function resetDefaultValues() {
      queryInput.value = [...Array(displayedHops.value).keys()].map((i: number) => {
        if (store.state.workspaceName === 'marclab') {
          if (i % 2) {
            return {
              key: i, value: [{ label: 'Type', operator: '=~', input: '' }], operator: '',
            };
          }
          return {
            key: i, value: [{ label: 'Label', operator: '=~', input: '' }], operator: '',
          };
        }
        return {
          key: i, value: [{ label: '', operator: '=~', input: '' }], operator: '',
        };
      });

      if (store.state.workspaceName === 'marclab') {
        edgeMutexs.value = {
          value: [{ label: 'Type', operator: '=~', input: '' }], operator: '',
        };
      } else {
        edgeMutexs.value = {
          value: [{ label: '', operator: '=~', input: '' }], operator: '',
        };
      }
    }

    watch([displayedHops], () => resetDefaultValues());

    onMounted(() => resetDefaultValues());

    function addField(index: number, edgeMutex = false) {
      if (edgeMutex) {
        edgeMutexs.value.value.push({ input: '', label: '', operator: '=~' });
      } else {
        queryInput.value[index].value.push({ input: '', label: '', operator: '=~' });
      }
    }

    function removeField(index: number, field: number, edgeMutex = false) {
      if (edgeMutex) {
        edgeMutexs.value.value.splice(field, 1);
      } else {
        queryInput.value[index].value.splice(field, 1);
      }
    }

    function isTextComparison(operator: string) {
      return ['==', '=~'].includes(operator);
    }

    function submitQuery() {
      loading.value = true;
      directionalEdges.value = true;
      const pathQueryTextComponents: string[] = [];

      // Loop through nodes and edges
      queryInput.value.forEach((input) => {
        // Add the starting string of each line of the query
        let currentString = '';
        const thisRoundIsNode = input.key % 2 === 0;
        const nodeOrEdgeNum = Math.floor(input.key / 2);

        if (input.key === 0) {
          currentString += `LET start_nodes = (FOR n0 in [${store.getters.nodeTableNames}][**] FILTER 1==1 `;
        } else if (!thisRoundIsNode) {
          currentString += `FOR n${nodeOrEdgeNum + 1}, e${nodeOrEdgeNum + 1} IN 1..1 ANY n${nodeOrEdgeNum} GRAPH '${store.state.networkName}' FILTER 1==1 `;
        }

        // Loop through each query piece
        input.value.forEach((queryPiece, index) => {
          // Add the right operator (AND for edge then node else, whatever op is defined)
          const operator = input.operator === 'NOT' ? 'AND NOT (' : input.operator;

          if (index === 0) {
            currentString += 'AND ( ';
          } else {
            currentString += `${operator} `;
          }

          // If no filter, do nothing
          if (queryPiece.label === '') {
            currentString += '1==1 ';
          } else {
            let property = thisRoundIsNode ? `n${nodeOrEdgeNum}.${queryPiece.label}` : `e${nodeOrEdgeNum + 1}.${queryPiece.label}`;
            property = isTextComparison(queryPiece.operator) ? `UPPER(${property})` : `TO_NUMBER(${property})`;
            const value = isTextComparison(queryPiece.operator) ? `UPPER('${queryPiece.input}')` : `TO_NUMBER(${queryPiece.input})`;
            currentString += `${property} ${queryPiece.operator} ${value} `;
          }

          if (index !== 0 && input.operator === 'NOT') {
            currentString += ')';
          }
        });
        currentString += ') ';

        // Add to the filter for edge mutex
        if (input.key === 1 && showSecondEdge.value) {
          currentString += 'AND (NOT POSITION(excluded_pairs, [n0, n1]))';
        }

        // Append any last required string
        if (input.key === 0) {
          // Add mutual exclusion query line
          if (showSecondEdge.value) {
            currentString += `RETURN n0) \nLET excluded_pairs = UNIQUE(FOR n0 in start_nodes FOR n1, e1, p1 IN 1..1 ANY n0 GRAPH '${store.state.networkName}' FILTER (`;

            // Add mutual exclusion filters
            const { operator } = edgeMutexs.value;
            edgeMutexs.value.value.forEach((queryPiece, index) => {
              if (index !== 0) {
                currentString += `${operator} `;
              }
              const property = isTextComparison(queryPiece.operator) ? `UPPER(e1.${queryPiece.label})` : `TO_NUMBER(e1.${queryPiece.label})`;

              const value = isTextComparison(queryPiece.operator) ? `UPPER('${queryPiece.input}')` : `TO_NUMBER(${queryPiece.input})`;

              currentString += `${property} ${queryPiece.operator} ${value} `;
            });

            // Add filter ending parenthesis
            currentString += ') ';

            currentString += 'RETURN p1.vertices) \nFOR n0 in start_nodes';
          } else {
            currentString += 'RETURN n0) \nFOR n0 in start_nodes';
          }
        }

        pathQueryTextComponents.push(currentString);
      });

      // Add return statement
      const itemsToReturn = Array(selectedHops.value).fill(0).map((_, index) => `e${index + 1}, n${index + 1}`).join(', ');
      pathQueryTextComponents.push(`RETURN [n0, ${itemsToReturn}]`);

      // Join each line of the query together
      const aqlQuery = pathQueryTextComponents.join('\n');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let newAQLNetwork: Promise<any[]> | undefined;
      try {
        newAQLNetwork = api.aql(store.state.workspaceName || '', aqlQuery);
      } catch (error) {
        // Add error message for user
        if (error.status === 400) {
          store.commit.setLoadError({
            message: error.statusText,
            href: 'https://multinet.app',
          });
        }
      }
      if (newAQLNetwork !== undefined) {
        newAQLNetwork.then((promise) => {
          if (promise.length !== 0) {
            // some data manipulation to show only start + end nodes
            const newNetwork: Network = { nodes: [], edges: [] };
            const endsNodesSet = new Set();
            const middleNodesSet = new Set();
            const middleNodesList: Node[] = [];
            const reconstructedPaths: ArangoPath[] = [];

            promise.forEach((path: (Node | Edge)[], val: number) => {
              const newPath: Edge = {
                _from: '', _to: '', _key: '', _id: '', _rev: '',
              };
              const reconstructedPath: ArangoPath = {
                vertices: [],
                edges: [],
              };

              path.forEach((nodeOrEdge, index) => {
                // Edges
                if (index % 2 !== 0) {
                  reconstructedPath.edges.push(nodeOrEdge as Edge);
                }

                if (index % 2 === 0) {
                  reconstructedPath.vertices.push(nodeOrEdge as Node);

                  if (index === 0) {
                    newPath._from = nodeOrEdge._id;
                    if (!endsNodesSet.has(nodeOrEdge._id)) { newNetwork.nodes.push(nodeOrEdge as Node); }
                    endsNodesSet.add(nodeOrEdge._id);
                  } else if (index < path.length - 1) {
                    if (!middleNodesSet.has(nodeOrEdge._id)) { middleNodesList.push(nodeOrEdge as Node); }
                    middleNodesSet.add(nodeOrEdge._id);
                  } else {
                    newPath._to = nodeOrEdge._id;
                    if (!endsNodesSet.has(nodeOrEdge._id)) { newNetwork.nodes.push(nodeOrEdge as Node); }
                    endsNodesSet.add(nodeOrEdge._id);
                  }
                }
              });

              // generate _key and _id
              newPath._key = val.toString();
              newPath._id = val.toString();
              newNetwork.edges.push(newPath);

              reconstructedPaths.push(reconstructedPath);
            });

            // Update state for use in intermediate node vis TODO
            store.commit.setConnectivityMatrixPaths({ nodes: middleNodesList, paths: reconstructedPaths });

            // Update state for showing intermediate node vis
            store.commit.toggleShowIntNodeVis(selectedHops.value > 1);

            // Update state with new network
            store.dispatch.aggregateNetwork(undefined);
            store.dispatch.updateNetwork({ network: newNetwork });
          } else {
            // Update state with empty network
            store.dispatch.aggregateNetwork(undefined);
            store.dispatch.updateNetwork({ network: { nodes: [], edges: [] } });
            store.commit.toggleShowIntNodeVis(false);
          }

          loading.value = false;
        });
      }
    }

    const dialog = ref(false);

    return {
      showMenu,
      showSecondEdge,
      hopsSelection,
      selectedHops,
      displayedHops,
      queryOptionItems,
      selectedVariables,
      selectedQueryOptions,
      nodeVariableItems,
      edgeVariableItems,
      nodeAttributeItems,
      edgeAttributeItems,
      submitQuery,
      loading,
      addField,
      removeField,
      queryInput,
      operatorOptionItems,
      edgeMutexs,
      dialog,
    };
  },
});
